import { Money } from '@hesabat/finance-core';

/** لوحات جاهزة: لون أساسى للترويسة والجداول، وثانوى للخطوط والإبراز. */
export const BRAND_PALETTES: readonly { name: string; primary: string; secondary: string }[] = [
  { name: 'كحلى وذهبى', primary: '#1f4e79', secondary: '#c9a227' },
  { name: 'زمردى', primary: '#0f5132', secondary: '#d4a017' },
  { name: 'عنابى', primary: '#7b1e3a', secondary: '#c8a165' },
  { name: 'أزرق بترولى', primary: '#0b6e99', secondary: '#f2994a' },
  { name: 'فحمى ومريمى', primary: '#2f3e46', secondary: '#84a98c' },
  { name: 'بنفسجى ملكى', primary: '#4a2c6f', secondary: '#e0b354' },
];

function channel(hex: string, offset: number): number {
  const value = parseInt(hex.slice(offset, offset + 2), 16) / 255;
  return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

/** الإضاءة النسبية بتعريف WCAG 2. */
export function luminance(hex: string): number {
  return 0.2126 * channel(hex, 1) + 0.7152 * channel(hex, 3) + 0.0722 * channel(hex, 5);
}

export function contrastRatio(a: string, b: string): number {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (light + 0.05) / (dark + 0.05);
}

/** لون النص فوق خلفية: الأبيض أو الأسود، أيهما أوضح — المالك يختار اللون لا التباين. */
export function readableTextOn(background: string): '#ffffff' | '#111111' {
  return contrastRatio(background, '#ffffff') >= contrastRatio(background, '#111111')
    ? '#ffffff'
    : '#111111';
}

const ONES = ['', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة'];
const TEENS = [
  'عشرة',
  'أحد عشر',
  'اثنا عشر',
  'ثلاثة عشر',
  'أربعة عشر',
  'خمسة عشر',
  'ستة عشر',
  'سبعة عشر',
  'ثمانية عشر',
  'تسعة عشر',
];
const TENS = ['', '', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'];
const HUNDREDS = [
  '',
  'مائة',
  'مائتان',
  'ثلاثمائة',
  'أربعمائة',
  'خمسمائة',
  'ستمائة',
  'سبعمائة',
  'ثمانمائة',
  'تسعمائة',
];

/** المفرد والمثنى والجمع (٣–١٠) والتمييز المفرد (١١ فأكثر) لكل مرتبة. */
const SCALES: readonly [string, string, string, string][] = [
  ['ألف', 'ألفان', 'آلاف', 'ألف'],
  ['مليون', 'مليونان', 'ملايين', 'مليون'],
  ['مليار', 'ملياران', 'مليارات', 'مليار'],
];

function below100(n: number): string {
  if (n < 10) return ONES[n] ?? '';
  if (n < 20) return TEENS[n - 10] ?? '';
  const one = ONES[n % 10] ?? '';
  const ten = TENS[Math.floor(n / 10)] ?? '';
  return one ? one + ' و' + ten : ten;
}

function below1000(n: number): string {
  const hundred = HUNDREDS[Math.floor(n / 100)] ?? '';
  const rest = below100(n % 100);
  return [hundred, rest].filter(Boolean).join(' و');
}

/** عدد صحيح غير سالب (نص أرقام حتى ١٢ خانة) بالحروف. */
export function integerInArabicWords(digits: string): string {
  const clean = digits.replace(/^0+/, '');
  if (clean === '') return 'صفر';
  if (clean.length > 12) return clean;
  const groups: number[] = [];
  for (let end = clean.length; end > 0; end -= 3) {
    groups.unshift(Number(clean.slice(Math.max(0, end - 3), end)));
  }
  const parts: string[] = [];
  groups.forEach((value, index) => {
    if (value === 0) return;
    const scale = SCALES[groups.length - index - 2];
    if (!scale) {
      parts.push(below1000(value));
    } else if (value === 1) {
      parts.push(scale[0]);
    } else if (value === 2) {
      parts.push(scale[1]);
    } else if (value <= 10) {
      parts.push(below1000(value) + ' ' + scale[2]);
    } else {
      parts.push(below1000(value) + ' ' + scale[3]);
    }
  });
  return parts.join(' و');
}

const CURRENCY_WORDS: Record<string, [string, string]> = {
  EGP: ['جنيه مصرى', 'قرش'],
  USD: ['دولار أمريكى', 'سنت'],
  EUR: ['يورو', 'سنت'],
  SAR: ['ريال سعودى', 'هللة'],
};

/** «فقط ألف ومائتان جنيه مصرى وخمسون قرش لا غير» — صياغة الفواتير المصرية المعتادة. */
export function amountInArabicWords(amount: string, currency: string): string {
  const fixed = Money.of(amount).abs().toFixed(2);
  const [whole = '0', fraction = '00'] = fixed.split('.');
  const [major, minor] = CURRENCY_WORDS[currency] ?? [currency, 'من مائة'];
  let text = 'فقط ' + integerInArabicWords(whole) + ' ' + major;
  if (fraction !== '00') text += ' و' + integerInArabicWords(fraction) + ' ' + minor;
  return text + ' لا غير';
}
